import PropTypes from 'prop-types';
import { HTMLAttributes, ReactNode, useState } from 'react';
import { RegisterOptions, useFormContext, useWatch } from 'react-hook-form';
import Text, { TextColor, TextVariant } from '../Text';
import {
  Container,
  FLoatLabel,
  HelperText,
  HelperTextOptional,
  IconContainer,
  InputContainer
} from './styles';

import {
  onlyEmailRegex,
  onlyFormatDateRegex,
  phoneSpecialCharacters,
  usPhoneRegex
} from 'src/helpers/formRegex';
import { Icon } from '../Icon';

export enum InputTextType {
  text = 'text',
  email = 'email',
  number = 'number',
  password = 'password',
  tel = 'tel',
  date = 'date'
}

export type InputTextOnChangeProps = {
  field: string;
  value: string | boolean;
};

export interface InputTextProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  name: string;
  refInput?: any;
  type: InputTextType;
  placeholder: string;
  value?: string;
  showLabel?: boolean;
  isDisabled?: boolean;
  isReadonly?: boolean;
  readOnlyColor?: keyof typeof TextColor;
  showOptionalLabel?: boolean;
  config?: RegisterOptions;
  endAndorment?: ReactNode;
  multiline?: boolean;
  rows?: number;
  defaultValue?: string;
  'data-testid'?: string;
  // Forwarded to the <input>, not the wrapper div like the other HTML attributes.
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  maxLength?: number;
  onChange?: ({ field, value }: InputTextOnChangeProps) => void;
}

const DEFAULT_VALIDATIONS = {
  onlyNumbers: () => true,
  minLength: () => true,
  maxLength: () => true,
  format: () => true
};

const InputText = (props: InputTextProps) => {
  const {
    refInput,
    name,
    type,
    placeholder,
    value,
    isDisabled,
    isReadonly,
    readOnlyColor,
    showOptionalLabel,
    showLabel,
    config,
    onChange,
    endAndorment,
    multiline,
    rows,
    defaultValue,
    'data-testid': dataTestId,
    inputMode,
    maxLength,
    ...htmlAttrs
  } = props;

  const {
    register,
    control,
    setValue,
    formState: { errors },
    clearErrors
  } = useFormContext();

  const text = useWatch({
    control,
    name,
    defaultValue
  });
  const [valueText, setValueText] = useState(value);
  const [isOpenToggleEye, setIsOpenToggleEye] = useState(false);

  const setFocusedOn = () => {
    const container = document.getElementById(`${name}_container`);
    if (container && !isReadonly) {
      container.setAttribute('data-focused', 'true');
    }
  };

  const setFocusedOff = () => {
    const container = document.getElementById(`${name}_container`);
    if (container && !isReadonly) {
      container.setAttribute('data-focused', 'false');
    }
  };

  const errorWithIndicator = () => {
    if (errors && name.indexOf('.') > 0) {
      const [indicatorField, nameField] = name.split('.');
      const errorObj: any = errors[indicatorField];
      const errorField = errorObj?.[nameField];
      if (errorField) {
        const field = errorField;
        if (field?.message) {
          return field.message;
        }
      }
    }
    return '';
  };

  const errorWithOutIndicator = () => {
    const message = errors[name]?.message;
    return message !== '' ? message : config?.required;
  };

  let hasError = Object.prototype.hasOwnProperty.call(errors, name);
  if (!hasError) {
    hasError = errorWithIndicator() !== '';
  }

  const configType = () => {
    if (type === InputTextType.email) {
      return {
        ...config,
        maxLength: {
          value: 100,
          message: '100 Characters Maximum'
        },
        pattern: {
          value: onlyEmailRegex,
          message: 'Invalid email address'
        }
      };
    }

    if (type === InputTextType.date) {
      return {
        ...config,
        validate: {
          ...DEFAULT_VALIDATIONS,
          format: (value: string) =>
            value === '' || onlyFormatDateRegex.test(value) || 'Invalid date format'
        }
      };
    }

    if (type === InputTextType.tel) {
      return {
        ...config,
        validate: {
          ...DEFAULT_VALIDATIONS,
          phoneValidation: (value: string) =>
            // only run validation if there is a value inserted in the field
            value
              ? usPhoneRegex.test(value) ||
                'Invalid phone number. Make sure to enter a valid US phone.'
              : undefined,
          minLength: (value: string) =>
            value
              ? value.trim().replace(phoneSpecialCharacters, '').length >= 10 ||
                'Phone number should have 10 digits minimum.'
              : undefined,
          maxLength: (value: string) =>
            value
              ? value.trim().replace(phoneSpecialCharacters, '').length <= 15 ||
                'Phone number should have 15 digits maximum.'
              : undefined
        }
      };
    }

    return config;
  };
  const configUpd = configType();

  const inputProps = register(name, {
    ...configUpd,
    disabled: isDisabled,
    onBlur: () => {
      if (valueText) {
        setValueText(valueText.trim());
        setValue(name, valueText.trim());
      }
      setFocusedOff();
    }
  } as any);

  const Label = () => {
    if (!showLabel) {
      return null;
    }

    const LabelComp = (
      <FLoatLabel aria-invalid={hasError ? 'true' : 'false'}>
        <Text variant={TextVariant.small} color={TextColor.primary}>
          {placeholder}
        </Text>
      </FLoatLabel>
    );

    if (text === undefined) return null;

    if (isReadonly && text !== '' && valueText !== '') return LabelComp;

    if (text === '' && valueText === undefined) return null;

    if (text !== '' && valueText === '') return LabelComp;

    if (valueText !== '') return LabelComp;

    if (text !== '' && valueText === undefined) return LabelComp;

    return null;
  };

  const onChanging = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    clearErrors(name);
    const text = e.target.value;
    setValueText(text);
    setValue(name, text);
    if (onChange) {
      onChange({ field: name, value: text });
    }
  };

  const inputType = () => {
    if (type === InputTextType.password) {
      return isOpenToggleEye ? InputTextType.text : type;
    }

    if (type === InputTextType.date) return InputTextType.text;

    return type;
  };

  const IconEye = () => {
    return (
      <IconContainer
        onClick={() => !isDisabled && setIsOpenToggleEye(!isOpenToggleEye)}
        role="button"
        aria-pressed="true"
        aria-label={`${!isOpenToggleEye ? 'show' : 'hide'} password`}
      >
        <Icon icon={isOpenToggleEye ? 'icon-eye-close' : 'icon-eye-open'} />
      </IconContainer>
    );
  };

  const setRef = refInput && { ref: refInput };

  const attrs = {
    ...inputProps,
    ...setRef,
    defaultValue,
    id: name,
    className: 'input-form-control',
    autoComplete: 'off',
    type: inputType(),
    inputMode,
    maxLength,
    placeholder: placeholder,
    'aria-invalid': !!hasError,
    'aria-required': !!configUpd?.required,
    'aria-disabled': isDisabled,
    'aria-placeholder': placeholder,
    'aria-label': name,
    'data-isclickable': typeof props.onClick === 'function',
    'data-testid': dataTestId,
    disabled: isDisabled,
    readOnly: isReadonly,
    onChange: onChanging,
    onFocus: setFocusedOn
  };
  return (
    <Container {...htmlAttrs} aria-invalid={!!hasError}>
      <InputContainer
        hasError={hasError}
        readOnlyColor={readOnlyColor}
        id={`${name}_container`}
        isDisabled={!!isDisabled}
        className="input-form"
        endAndorment={Boolean(endAndorment)}
        multiline={multiline}
        aria-disabled={!!isDisabled}
      >
        <Label />
        {multiline ? <textarea {...attrs} rows={rows} /> : <input {...attrs} />}
        {endAndorment}
      </InputContainer>
      {type === InputTextType.password && IconEye()}
      {!isDisabled && hasError && (
        <HelperText className="HelperText">
          <Text variant={TextVariant.small} color={TextColor.error}>
            {errorWithIndicator() !== '' ? errorWithIndicator() : errorWithOutIndicator()}
          </Text>
        </HelperText>
      )}
      {!configUpd?.required && !isDisabled && !isReadonly && showOptionalLabel && (
        <HelperTextOptional className="HelperTextOptional" hasError={hasError}>
          <Text variant={TextVariant.small} color={TextColor.muted}>
            Optional
          </Text>
        </HelperTextOptional>
      )}
    </Container>
  );
};

export const InputTextPropTypes = {
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf<InputTextType>(Object.values(InputTextType)).isRequired,
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.string,
  isDisabled: PropTypes.bool,
  isReadonly: PropTypes.bool,
  readOnlyColor: PropTypes.oneOf<TextColor>(Object.values(TextColor)),
  showOptionalLabel: PropTypes.bool,
  showLabel: PropTypes.bool,
  onChange: PropTypes.func,
  endAndorment: PropTypes.node,
  rows: PropTypes.number,
  /** Follow the reference of react-hook-form.com */
  config: PropTypes.object
};

export const InputTextDefaultProps = {
  name: 'inputName',
  type: InputTextType.text,
  placeholder: 'Placeholder',
  value: '',
  isDisabled: false,
  isReadonly: false,
  showOptionalLabel: true,
  showLabel: true,
  endAndorment: null,
  multiline: false,
  config: {},
  defaultValue: ''
};

InputText.propTypes = InputTextPropTypes;
InputText.defaultProps = InputTextDefaultProps;

export default InputText;
